import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import DuckCrest from "@/components/DuckCrest";

const fmtGBP = (a) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(a);

export default function GiftClaimPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [gift, setGift] = useState(null);
  const [error, setError] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [redeemed, setRedeemed] = useState(null);

  useEffect(() => {
    if (!code) return;
    api.get(`/gifts/${code}`)
      .then(({ data }) => setGift(data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "Gift not found"));
  }, [code]);

  const claim = async () => {
    if (!user) {
      sessionStorage.setItem("dr_pending_gift_claim", code);
      navigate("/login");
      return;
    }
    setClaiming(true);
    try {
      const { data } = await api.post("/gifts/redeem", { code });
      setRedeemed(data);
      toast.success(`+${data.tiles_credited} tiles credited`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't redeem");
    } finally {
      setClaiming(false);
    }
  };

  if (loading) return null;

  return (
    <div className="gift-claim-page" data-testid="gift-claim-page">
      <div className="gift-claim-card">
        <div className="gift-claim-crest">
          <DuckCrest size={64} color="rgba(26,22,20,0.92)" />
        </div>
        <div className="gift-claim-eyebrow">DIGITAL GIFT PASS</div>
        <h1 className="gift-claim-title">A gift from Duck & Rice</h1>
        <div className="dr-modal-rule" />

        {error && (
          <p className="gift-claim-error" data-testid="gift-claim-error">{error}</p>
        )}

        {gift && !redeemed && (
          <>
            <p className="gift-claim-line">
              <span className="gift-claim-label">FROM</span>
              <strong>{gift.sender_name}</strong>
            </p>
            <p className="gift-claim-line">
              <span className="gift-claim-label">TO</span>
              <strong>{gift.recipient_name}</strong>
            </p>
            {gift.message && (
              <blockquote className="gift-claim-msg">"{gift.message}"</blockquote>
            )}
            <div className="gift-claim-amount" data-testid="gift-claim-amount">
              <div className="gift-claim-amount-big">{fmtGBP(gift.amount_gbp)}</div>
              <div className="gift-claim-amount-tiles">= {gift.tiles_value} TILES</div>
            </div>

            {gift.status === "redeemed" ? (
              <p className="gift-claim-redeemed" data-testid="gift-already-redeemed">
                This gift has already been redeemed.
              </p>
            ) : (
              <button
                className="dr-btn-primary"
                style={{ width: "100%", marginTop: 18 }}
                onClick={claim}
                disabled={claiming}
                data-testid="gift-claim-btn"
              >
                {claiming ? "CLAIMING…" : (user ? "CLAIM TILES" : "SIGN IN TO CLAIM")}
              </button>
            )}
          </>
        )}

        {redeemed && (
          <div className="gift-claim-success" data-testid="gift-claim-success">
            <div className="gift-claim-amount-big" style={{ color: "var(--gold)" }}>
              +{redeemed.tiles_credited}
            </div>
            <div className="gift-claim-amount-tiles">TILES CREDITED</div>
            <button
              className="dr-btn-primary"
              style={{ width: "100%", marginTop: 22 }}
              onClick={() => navigate("/")}
              data-testid="gift-go-home-btn"
            >
              ENTER THE CLUB
            </button>
          </div>
        )}

        <button
          type="button"
          className="dr-btn-ghost"
          style={{ width: "100%", marginTop: 12 }}
          onClick={() => navigate("/")}
        >
          {user ? "BACK TO THE CLUB" : "I'M ALREADY A MEMBER"}
        </button>
      </div>
    </div>
  );
}
